/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/Dataset/Dataset.tsx.
 */

import React, { useState } from "react";
import { toast } from "react-toastify";
import "./Dataset.css";
import "../../../Admin/Materials/SubmissionDetail/AdminSubmissionDetail.css";
import "../../../../../components/common/ConfirmModal.css";
import SubmissionDetailLayout from "../../../../../components/submission/SubmissionDetailLayout";
import { useDatasetData } from "./hooks/useDatasetData";
import { updateSubmissionStage } from "../../../../../services/submissionService";
import Button from "../../../../../components/common/Button";

interface DatasetProps {
  submissionId?: string;
  onNextStage: () => void;
}

const Dataset: React.FC<DatasetProps> = ({ submissionId, onNextStage }) => {
  const { data, loading } = useDatasetData(submissionId);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  if (loading) return <p className="loading-text">Loading dataset…</p>;
  if (!data) return <p>No data available</p>;

  const handleSubmit = async () => {
    if (!submissionId || submitting) return;
    setSubmitting(true);
    try {
      await updateSubmissionStage(submissionId, "SUBMITTED");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to submit dataset", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to submit dataset. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessDismiss = () => {
    setShowSuccessModal(false);
    onNextStage();
  };

  return (
    <>
      <SubmissionDetailLayout
        bannerTitle="Dataset Overview"
        submission={data}
        showReviewFlagHistory={false}
        showReviewStatus={false}
      >
        <div className="button-row">
          <Button
            label={submitting ? "Submitting…" : "Submit Dataset"}
            onClick={() => void handleSubmit()}
            disabled={submitting}
          />
        </div>
      </SubmissionDetailLayout>

      {showSuccessModal && (
        <div
          className="modal-overlay dataset-submit-success-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dataset-submit-success-title"
        >
          <div className="modal-card dataset-submit-success-modal fade-in">
            <div className="modal-header">
              <h2 id="dataset-submit-success-title">Dataset submitted</h2>
              <button
                type="button"
                className="modal-close"
                onClick={handleSuccessDismiss}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body dataset-submit-success-body">
              <div className="dataset-submit-success-icon" aria-hidden="true">
                ✓
              </div>
              <p>
                Your dataset has been submitted successfully. You can view it
                under Submitted Datasets.
              </p>
            </div>
            <div className="modal-footer">
              <Button
                label="OK"
                onClick={handleSuccessDismiss}
                type="button"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** React component — Dataset. */
export default Dataset;
